import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';

import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { AgrDatePipe, LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  merge,
  skipWhile,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Delivery } from 'src/app/delivery/models/delivery.model';
import { getDeliveries } from 'src/app/delivery/store/selectors/delivery.selectors';
import { FinalSetupSettings } from 'src/app/settings/models/setting.model';
import { getFinalSetupSettings } from 'src/app/settings/store/selectors/setting.selector';
import { VatPercentages } from 'src/app/shared/constants';
import { DateHelper } from 'src/app/shared/helpers/dateHelper';
import Utils from 'src/app/shared/utils';

import {
  AdditionalInvoiceDataRow,
  DeliveryDataRow,
  DeliveryPrintRow,
  DiscountDataRow,
  FinalSetupDataRow,
  FinalSetupPrintRow,
  FinalSetupRowType,
  FixedPriceCorrectionPrintRow,
} from './models/final-setup.model';

import * as deliveryActions from '../../../delivery/store/actions/delivery.actions';
import { Cycle, DayOldChick } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getCycles,
  getLinkedSlaughterInvoices,
  getSelectedCycle,
  getUnlinkedSlaughterInvoices,
} from '../../store/selectors/cycle.selectors';
import {
  EditAdditionalInvoiceMode,
  FinalSetupEditAdditionalInvoiceComponent,
} from '../final-setup-edit-additional-invoice/final-setup-edit-additional-invoice.component';
import { FinalSetupEditItemComponent } from '../final-setup-edit-item/final-setup-edit-item.component';
import { FinalSetupPrintComponent } from '../final-setup-print/final-setup-print.component';
import { ImportFromSlaughterInvoiceComponent } from '../import-from-slaughter-invoice/import-from-slaughter-invoice.component';

@Component({
  selector: 'app-final-setup',
  templateUrl: './final-setup.component.html',
  styleUrls: [],
  imports: [
    MatButton,
    MatIcon,
    NgIf,
    SummaryCardComponent,
    MatSlideToggle,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AgrNumberDirective,
    MatSelect,
    MatOption,
    NgFor,
    TableComponent,
    FinalSetupPrintComponent,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class FinalSetupComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialogService = inject(DialogService);
  private readonly datePipe = inject(AgrDatePipe);
  private readonly brandingService = inject(BrandingService);

  private readonly destroyed$ = new Subject<void>();

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  private readonly selectedCycle$: Observable<Cycle> = this.store
    .select(getSelectedCycle)
    .pipe(skipWhile((c) => !c?.id));
  private readonly cycles$: Observable<Cycle[]> = this.store.select(getCycles);
  private readonly finalSetupSettings$: Observable<FinalSetupSettings> =
    this.store.select(getFinalSetupSettings);
  private readonly deliveries$: Observable<Delivery[]> = this.store.select(getDeliveries);
  private readonly chickDeliveries$: Observable<FinalSetupDataRow[]>;
  private readonly departures$: Observable<FinalSetupDataRow[]>;
  private readonly discounts$: Observable<DiscountDataRow[]>;
  private readonly feedDeliveries$: Observable<DeliveryDataRow[]>;
  private readonly additionalInvoices$: Observable<AdditionalInvoiceDataRow[]>;

  public readonly fixedPriceFormGroup = new FormGroup({
    pricePer100Kg: new FormControl<number>(null),
    invoiceNumber: new FormControl<string>(null),
    invoiceDate: new FormControl<Date>(null),
    vatPercentage: new FormControl<number>(null),
  });
  public readonly vatPercentages = VatPercentages;
  public readonly feedDeliveriesAggregatePrint$: Observable<FinalSetupPrintRow>;
  public readonly feedDeliveriesPrint$: Observable<DeliveryPrintRow[]>;
  public readonly feedDeliveriesTotalPrint$: Observable<DeliveryPrintRow>;
  public readonly discountsPrint$: Observable<FinalSetupPrintRow[]>;
  public readonly chickDeliveriesPrint$: Observable<FinalSetupPrintRow[]>;
  public readonly departuresPrint$: Observable<FinalSetupPrintRow[]>;
  public readonly totalsExcludingDeparturesPrint$: Observable<FinalSetupPrintRow>;
  public readonly additionalInvoicesPrint$: Observable<FinalSetupPrintRow[]>;
  public readonly additionalInvoicesTotalPrint$: Observable<FinalSetupPrintRow>;
  public readonly totalsPrint$: Observable<FinalSetupPrintRow>;
  public readonly showTotalsExcludingDepartures$: Observable<boolean>;
  public readonly feedDeliveryEndDateControl = new FormControl<Date>(null);
  public readonly fixedPriceCorrectionPrint$: Observable<FixedPriceCorrectionPrintRow | undefined>;

  chickDeliveriesTableConfig: TableConfig;
  departuresTableConfig: TableConfig;

  toggleValueFixedPrice: boolean;
  toggleValueChickDeliveries: boolean;
  toggleIncludeInCalculationsFeedDeliveries: boolean;
  toggleValueDepartures: boolean;
  toggleValueDiscounts: boolean;

  isAgrifirm: boolean = this.brandingService.isTenant('Agrifirm');

  public readonly linkedSlaughterInvoices$ = this.store.select(getLinkedSlaughterInvoices);
  public readonly unlinkedSlaughterInvoices$ = this.store.select(getUnlinkedSlaughterInvoices);

  summaryCardActions: SummaryCardAction[] = [
    {
      id: '',
    },
  ];

  private readonly finalSetupTableColumns = [
    {
      id: 'invoiceDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('invoice-date'),
    },
    {
      id: 'invoiceNumber',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('invoice-number'),
    },
    {
      id: 'amount',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('amount-excluding-vat'),
      digitsInfo: '1.02-02',
    },
    {
      id: 'vatPercentage',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('vat'),
    },
    {
      id: 'amountIncludingVat',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('amount-including-vat'),
      digitsInfo: '1.02-02',
    },
  ];

  private readonly discountColumns: TableColumn[] = [
    {
      id: 'label',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('label'),
    },
    {
      id: 'description',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('discount-description'),
    },
    ...this.finalSetupTableColumns,
  ];

  private readonly feedDeliveriesColumns: TableColumn[] = [
    {
      id: 'deliveryDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('delivery-date'),
    },
    {
      id: 'deliveryId',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('delivery-number'),
    },
    {
      id: 'baseQuantity',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('base-quantity'),
    },
    ...this.finalSetupTableColumns,
  ];

  private readonly additionalInvoiceColumns: TableColumn[] = [
    {
      id: 'description',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('discount-description'),
    },
    ...this.finalSetupTableColumns,
  ];

  private readonly finalSetupTableConfig: TableConfig = {
    columns: this.finalSetupTableColumns,
    pagination: false,
    dataSource: null,
    selectColumn: false,
    actionColumn: false,
    scrolling: true,
    disableCards: true,
    wrapperWidth: '100%',
    activeSort: {
      id: 'invoiceDate',
      start: 'desc',
      disableClear: false,
    },
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        callback: (row: FinalSetupDataRow) => this.editItem(row),
        icon: 'edit',
        disabled: (row: FinalSetupDataRow) => !row.isEditable,
      },
    ],
  };

  discountsTableConfig: TableConfig = {
    ...this.finalSetupTableConfig,
    columns: this.discountColumns,
  };

  feedDeliveriesTableConfig: TableConfig = {
    ...this.finalSetupTableConfig,
    columns: this.feedDeliveriesColumns,
  };

  additionalInvoicesTableConfig: TableConfig = {
    ...this.finalSetupTableConfig,
    columns: this.additionalInvoiceColumns,
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        callback: (row: AdditionalInvoiceDataRow) => this.editAdditionalInvoice(row),
        icon: 'edit',
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        callback: (row: AdditionalInvoiceDataRow) => this.deleteAdditionalInvoice(row),
        icon: 'delete',
        disabled: () => false,
      },
    ],
  };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.selectedCycle$
      .pipe(withLatestFrom(this.finalSetupSettings$, this.cycles$), take(1))
      .subscribe(([selectedCycle, finalSetupSettings, cycles]) => {
        const deliveryDateFrom = this.getDeliveryDateFrom(selectedCycle.cycleNumber, cycles);

        // reset the delivery filter values (in case it is manually updated in the Deliveries screen)
        this.store.dispatch(
          deliveryActions.setDeliveriesFilter({
            startDate: deliveryDateFrom.toISOString(),
            endDate: DateHelper.nowUTC().toISOString(),
          }),
        );

        this.toggleValueChickDeliveries =
          selectedCycle.finalSetup?.includeChickDeliveries ?? finalSetupSettings.chickDeliveries;
        this.toggleIncludeInCalculationsFeedDeliveries =
          selectedCycle.finalSetup?.includeFeedDeliveries ?? finalSetupSettings.feedDeliveries;
        this.toggleValueDepartures =
          selectedCycle.finalSetup?.includeDepartures ?? finalSetupSettings.departures;
        this.toggleValueDiscounts =
          selectedCycle.finalSetup?.includeDiscounts ?? finalSetupSettings.discounts;

        const cycleFeedDeliveryEndDate = selectedCycle.finalSetup?.cycleFeedDeliveryEndDate;
        if (cycleFeedDeliveryEndDate) {
          this.feedDeliveryEndDateControl.setValue(new Date(cycleFeedDeliveryEndDate), {
            emitEvent: false,
          });
        }

        const fixedPrice = selectedCycle.finalSetup?.fixedPrice;
        this.toggleValueFixedPrice = !!fixedPrice?.pricePer100Kg;
        if (fixedPrice) {
          this.fixedPriceFormGroup.patchValue(
            {
              ...fixedPrice,
              invoiceDate: fixedPrice.invoiceDate ? new Date(fixedPrice.invoiceDate) : null,
            },
            { emitEvent: false },
          );
        }

        this.fetchSlaughterInvoices(selectedCycle);
      });

    this.feedDeliveries$ = combineLatest([
      this.deliveries$,
      this.selectedCycle$,
      this.cycles$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([deliveries, selectedCycle, cycles]) => {
        if (deliveries.length > 0 && selectedCycle && cycles) {
          const deliveryDataRows: DeliveryDataRow[] = [];
          const deliveryDateFrom = this.getDeliveryDateFrom(selectedCycle.cycleNumber, cycles);
          const deliveryDateTo = this.getDeliveryDateTo(selectedCycle);

          deliveries.forEach((delivery) => {
            const deliveryDate = new Date(delivery.deliveryDate);
            const deliveryDateFromValid = deliveryDate > deliveryDateFrom;
            const deliveryDateToValid = deliveryDate <= deliveryDateTo;

            if (deliveryDateFromValid && deliveryDateToValid) {
              const isManualDelivery = delivery.deliveryType === 'Manual';

              delivery.deliveryLines.forEach((deliveryLine) => {
                const showDeliveryLine = deliveryLine.showInFinalSetup !== false;

                if (deliveryLine.unitNettoPrice !== null && showDeliveryLine) {
                  const amount = deliveryLine.nettoAmount;
                  const vatPercentage = deliveryLine.vatPercentage;
                  const amountIncludingVat = deliveryLine.brutoAmount;
                  const vatAmount = deliveryLine.vatAmount;

                  deliveryDataRows.push({
                    id: delivery.id,
                    invoiceNumber: deliveryLine.externalInvoiceId,
                    vatPercentage,
                    vatAmount,
                    amount,
                    amountIncludingVat,
                    invoiceDate: deliveryLine.invoiceDate
                      ? new Date(deliveryLine.invoiceDate)
                      : null,
                    productId: deliveryLine.externalProductId,
                    productName: deliveryLine.productTitle,
                    baseQuantity: deliveryLine.baseQuantity,
                    isEditable: isManualDelivery,
                    finalSetupRowType: FinalSetupRowType.FeedDeliveries,
                    deliveryDate,
                    deliveryId: delivery.externalDeliveryId,
                  });
                }
              });
            }
          });

          return this.sortByDate(deliveryDataRows);
        }

        return [];
      }),
    );

    this.chickDeliveries$ = this.selectedCycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        let chickDeliveriesDataRows: FinalSetupDataRow[] = [];

        if (selectedCycle?.dayOldChicks?.length > 0) {
          chickDeliveriesDataRows = selectedCycle.dayOldChicks.map((dayOldChicks) => ({
            id: dayOldChicks.id,
            amountIncludingVat: this.calculateAmountIncludingVatChickDelivery(dayOldChicks),
            invoiceNumber: dayOldChicks.invoiceNumber,
            vatPercentage: dayOldChicks.vatPercentage,
            amount: this.calculateAmountChickDelivery(dayOldChicks),
            invoiceDate: dayOldChicks.invoiceDate ? new Date(dayOldChicks.invoiceDate) : null,
            isEditable: true,
            finalSetupRowType: FinalSetupRowType.ChicksDeliveries,
          }));
        }

        return this.sortByDate(chickDeliveriesDataRows);
      }),
    );

    this.departures$ = combineLatest([this.selectedCycle$, this.linkedSlaughterInvoices$]).pipe(
      takeUntil(this.destroyed$),
      map(([selectedCycle, linkedSlaughterInvoices]) => {
        let departuresDataRows: FinalSetupDataRow[] = [];

        if (selectedCycle?.departures?.length > 0) {
          departuresDataRows = selectedCycle.departures
            .filter(
              (departure) =>
                !departure.linkedSlaughterReportId && departure.showInFinalSetup !== false,
            )
            .map((departure) => ({
              id: departure.id,
              amountIncludingVat: this.calculateAmountIncludingVat(
                departure.revenue,
                departure.vatPercentage,
              ),
              invoiceNumber: departure.invoiceNumber,
              vatPercentage: departure.vatPercentage,
              amount: departure.revenue,
              invoiceDate: departure.invoiceDate ? new Date(departure.invoiceDate) : null,
              isEditable: !departure.linkedSlaughterReportId,
              finalSetupRowType: FinalSetupRowType.Departures,
            }));
        }

        if (linkedSlaughterInvoices?.length > 0) {
          const importedInvoices: FinalSetupDataRow[] = linkedSlaughterInvoices.map((invoice) => ({
            id: invoice.id,
            amountIncludingVat: invoice.amountIncludingVat,
            invoiceNumber: invoice.invoiceId,
            vatPercentage: invoice.vatPercentage,
            amount: invoice.amount,
            invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate) : null,
            finalSetupRowType: FinalSetupRowType.Departures,
          }));

          departuresDataRows.push(...importedInvoices);
        }

        return this.sortByDate(departuresDataRows);
      }),
    );

    this.discounts$ = this.selectedCycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        let discountsDataRows: DiscountDataRow[] = [];

        if (selectedCycle?.discounts?.length > 0) {
          discountsDataRows = selectedCycle.discounts
            .filter((discount) => discount.showInFinalSetup)
            .map((discount) => ({
              id: discount.id,
              label: this.locale.translate(
                'discount-item.labelType-' + discount.label.toString().toLowerCase(),
              ),
              description: discount.description,
              invoiceDate: discount.invoiceDate ? new Date(discount.invoiceDate) : null,
              invoiceNumber: discount.invoiceNumber,
              amount: discount.amount,
              vatPercentage: discount.vatPercentage,
              amountIncludingVat: this.calculateAmountIncludingVat(
                discount.amount,
                discount.vatPercentage,
              ),
              showInFinalSetup: discount.showInFinalSetup,
              isEditable: !discount.linkedSlaughterReportId,
              finalSetupRowType: FinalSetupRowType.Discounts,
            }));
        }

        return this.sortByDate(discountsDataRows);
      }),
    );

    this.additionalInvoices$ = this.selectedCycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        const additionalInvoicesWithCalculatedAmounts: AdditionalInvoiceDataRow[] = (
          selectedCycle?.finalSetup?.additionalInvoices ?? []
        ).map((inv) => ({
          ...inv,
          amountIncludingVat: this.calculateAmountIncludingVat(inv.amount, inv.vatPercentage),
        }));

        return this.sortByDate(additionalInvoicesWithCalculatedAmounts);
      }),
    );

    this.feedDeliveriesAggregatePrint$ = this.feedDeliveries$.pipe(
      map((feedDeliveries) => {
        const amountsTotal = this.sumAmounts(feedDeliveries);
        const amountsIncludingVatTotal = this.sumAmountsIncludingVat(feedDeliveries);
        const vatAmount = amountsIncludingVatTotal - amountsTotal;

        const aggregate: FinalSetupPrintRow = {
          amount: amountsTotal,
          amountIncludingVat: amountsIncludingVatTotal,
          vatAmount,
        };

        return aggregate;
      }),
    );

    this.feedDeliveriesPrint$ = this.feedDeliveries$.pipe(
      map((feedDeliveries) => {
        if (feedDeliveries.length === 0) {
          return [];
        }

        feedDeliveries.forEach((d) => {
          if (!d.invoiceNumber) {
            d.invoiceNumber = '';
          }
        });

        const result: DeliveryPrintRow[] = [];
        const groups = this.groupItemsByKey(feedDeliveries, (fd) => fd.invoiceNumber);
        Object.keys(groups).forEach((key) => {
          const group = groups[key];

          if (key) {
            result.push({
              amount: this.sumAmounts(group),
              amountIncludingVat: this.sumAmountsIncludingVat(group),
              vatAmount: this.sumVatAmounts(group),
              baseQuantity: this.sumBaseQuantity(group),
              invoiceNumber: group[0].invoiceNumber,
              invoiceDate: group[0].invoiceDate,
              invoiceDateString: this.datePipe.transform(group[0].invoiceDate, 'dd-MM-yyyy'),
              products: group,
            });
          } else {
            // do not group the deliveries which do not have a valid invoice number
            group.forEach((d: DeliveryDataRow) => {
              result.push({
                amount: d.amount,
                amountIncludingVat: d.amountIncludingVat,
                vatAmount: d.vatAmount,
                baseQuantity: d.baseQuantity,
                invoiceNumber: d.invoiceNumber,
                invoiceDate: d.invoiceDate,
                invoiceDateString: this.datePipe.transform(d.invoiceDate, 'dd-MM-yyyy'),
                products: [d],
              });
            });
          }
        });

        return this.sortByDate(result);
      }),
    );

    this.feedDeliveriesTotalPrint$ = this.feedDeliveriesPrint$.pipe(
      map((feedDeliveriesPrint) => {
        const total: DeliveryPrintRow = {
          description: 'Eindtotaal voer',
          amount: this.sumAmounts(feedDeliveriesPrint),
          amountIncludingVat: this.sumAmountsIncludingVat(feedDeliveriesPrint),
          vatAmount: this.sumVatAmounts(feedDeliveriesPrint),
          baseQuantity: this.sumBaseQuantity(feedDeliveriesPrint),
        } as DeliveryPrintRow;
        return total;
      }),
    );

    this.discountsPrint$ = this.discounts$.pipe(
      map((discounts) => {
        const discountsPrint: FinalSetupPrintRow[] = [];
        discounts.forEach((discount) => {
          const vatAmount = discount.amountIncludingVat - discount.amount;
          const discountDescription = discount.description
            ? discount.label + ' - ' + discount.description
            : discount.label;
          discountsPrint.push({
            invoiceDateString: this.datePipe.transform(discount.invoiceDate, 'dd-MM-yyyy'),
            invoiceNumber: discount.invoiceNumber,
            description: discountDescription,
            amount: -discount.amount,
            amountIncludingVat: -discount.amountIncludingVat,
            vatAmount: -vatAmount,
          });
        });

        return discountsPrint;
      }),
    );

    this.departuresPrint$ = this.departures$.pipe(
      map((departures) => {
        if (departures.length === 0) {
          return [];
        }

        departures.forEach((d) => {
          if (!d.invoiceNumber) {
            d.invoiceNumber = '';
          }
        });

        const departuresPrint: FinalSetupPrintRow[] = [];
        const groups = this.groupItemsByKey(departures, (d) => d.invoiceNumber);

        Object.keys(groups).forEach((key) => {
          const group = groups[key];

          if (key) {
            const vatAmount = this.sumAmountsIncludingVat(group) - this.sumAmounts(group);

            departuresPrint.push({
              invoiceDateString: this.datePipe.transform(group[0].invoiceDate, 'dd-MM-yyyy'),
              invoiceNumber: group[0].invoiceNumber,
              description: 'Afrekening',
              amount: -this.sumAmounts(group),
              amountIncludingVat: -this.sumAmountsIncludingVat(group),
              vatAmount: -vatAmount,
            } as FinalSetupDataRow);
          } else {
            // do not group the departures which do not have a valid invoice number
            group.forEach((departure: FinalSetupDataRow) => {
              const vatAmount = departure.amountIncludingVat - departure.amount;

              departuresPrint.push({
                invoiceDateString: this.datePipe.transform(departure.invoiceDate, 'dd-MM-yyyy'),
                invoiceNumber: departure.invoiceNumber,
                description: 'Afrekening',
                amount: -departure.amount,
                amountIncludingVat: -departure.amountIncludingVat,
                vatAmount: -vatAmount,
              } as FinalSetupDataRow);
            });
          }
        });

        return departuresPrint;
      }),
    );

    this.chickDeliveriesPrint$ = this.chickDeliveries$.pipe(
      map((chickDeliveries) => {
        const chickDeliveriesPrint: FinalSetupPrintRow[] = [];
        chickDeliveries.forEach((chickDelivery) => {
          const vatAmount = chickDelivery.amountIncludingVat - chickDelivery.amount;
          chickDeliveriesPrint.push({
            invoiceDateString: this.datePipe.transform(chickDelivery.invoiceDate, 'dd-MM-yyyy'),
            invoiceNumber: chickDelivery.invoiceNumber,
            amount: chickDelivery.amount,
            amountIncludingVat: chickDelivery.amountIncludingVat,
            vatAmount,
          });
        });

        return chickDeliveriesPrint;
      }),
    );

    this.additionalInvoicesPrint$ = this.additionalInvoices$.pipe(
      map((additionalInvoices) => {
        const additionalInvoicesPrint: FinalSetupPrintRow[] = [];
        additionalInvoices.forEach((additionalInvoice) => {
          const vatAmount = additionalInvoice.amountIncludingVat - additionalInvoice.amount;
          additionalInvoicesPrint.push({
            invoiceDateString: this.datePipe.transform(additionalInvoice.invoiceDate, 'dd-MM-yyyy'),
            invoiceNumber: additionalInvoice.invoiceNumber,
            amount: additionalInvoice.amount,
            amountIncludingVat: additionalInvoice.amountIncludingVat,
            description: additionalInvoice.description,
            vatAmount,
          });
        });

        return additionalInvoicesPrint;
      }),
    );

    this.additionalInvoicesTotalPrint$ = this.additionalInvoicesPrint$.pipe(
      map(
        (additionalInvoices) =>
          ({
            amount: this.sumAmounts(additionalInvoices),
            amountIncludingVat: this.sumAmountsIncludingVat(additionalInvoices),
            vatAmount: this.sumVatAmounts(additionalInvoices),
          }) as FinalSetupPrintRow,
      ),
    );

    this.fixedPriceCorrectionPrint$ = combineLatest([
      this.selectedCycle$,
      this.feedDeliveriesTotalPrint$,
    ]).pipe(
      map(([selectedCycle, feedDeliveriesTotal]) => {
        const fixedPrice = selectedCycle?.finalSetup?.fixedPrice;

        if (!fixedPrice?.pricePer100Kg) {
          return undefined;
        }

        const totalFeedCost = feedDeliveriesTotal.amount;
        const totalFeedCostBasedOnFixedPrice =
          (feedDeliveriesTotal.baseQuantity / 100) * fixedPrice.pricePer100Kg;
        const fixedPriceCorrection = -(totalFeedCost - totalFeedCostBasedOnFixedPrice);
        const fixedPriceCorrectionIncludingVat = this.calculateAmountIncludingVat(
          fixedPriceCorrection,
          fixedPrice.vatPercentage,
        );

        return {
          pricePer100Kg: fixedPrice.pricePer100Kg,
          amount: fixedPriceCorrection,
          amountIncludingVat: fixedPriceCorrectionIncludingVat,
          vatAmount: fixedPriceCorrectionIncludingVat - fixedPriceCorrection,
          invoiceDateString: this.datePipe.transform(fixedPrice.invoiceDate, 'dd-MM-yyyy'),
          invoiceNumber: fixedPrice.invoiceNumber,
        };
      }),
    );

    this.totalsExcludingDeparturesPrint$ = combineLatest([
      this.feedDeliveriesAggregatePrint$,
      this.discountsPrint$,
      this.chickDeliveriesPrint$,
      this.fixedPriceCorrectionPrint$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([feedDeliveriesAggregate, discounts, chickDeliveries, fixedPriceCorrection]) => {
        let amountsTotal = 0;
        let amountsIncludingVatTotal = 0;
        let vatAmountsTotal = 0;

        if (this.toggleValueDiscounts) {
          amountsTotal += this.sumAmounts(discounts);
          amountsIncludingVatTotal += this.sumAmountsIncludingVat(discounts);
          vatAmountsTotal += this.sumVatAmounts(discounts);
        }

        if (this.toggleValueChickDeliveries) {
          amountsTotal += this.sumAmounts(chickDeliveries);
          amountsIncludingVatTotal += this.sumAmountsIncludingVat(chickDeliveries);
          vatAmountsTotal += this.sumVatAmounts(chickDeliveries);
        }

        if (this.toggleIncludeInCalculationsFeedDeliveries) {
          amountsTotal += this.sumAmounts([feedDeliveriesAggregate]);
          amountsIncludingVatTotal += this.sumAmountsIncludingVat([feedDeliveriesAggregate]);
          vatAmountsTotal += this.sumVatAmounts([feedDeliveriesAggregate]);

          if (fixedPriceCorrection) {
            amountsTotal += fixedPriceCorrection.amount;
            amountsIncludingVatTotal += fixedPriceCorrection.amountIncludingVat;
            vatAmountsTotal += fixedPriceCorrection.vatAmount;
          }
        }

        return {
          amount: amountsTotal,
          amountIncludingVat: amountsIncludingVatTotal,
          vatAmount: vatAmountsTotal,
        } as FinalSetupPrintRow;
      }),
    );

    this.totalsPrint$ = combineLatest([
      this.totalsExcludingDeparturesPrint$,
      this.departuresPrint$,
      this.additionalInvoicesTotalPrint$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([totalsExcludingDepartures, departures, additionalInvoices]) => {
        const departuresAmountsIncludingVat = this.toggleValueDepartures
          ? this.sumAmountsIncludingVat(departures)
          : 0;
        let totalAmountIncludingVat =
          -departuresAmountsIncludingVat - totalsExcludingDepartures.amountIncludingVat;

        if (additionalInvoices) {
          totalAmountIncludingVat -= additionalInvoices.amountIncludingVat;
        }

        return {
          amountIncludingVat: totalAmountIncludingVat,
        } as FinalSetupPrintRow;
      }),
    );

    this.showTotalsExcludingDepartures$ = combineLatest([
      this.feedDeliveries$,
      this.chickDeliveries$,
      this.discounts$,
    ]).pipe(
      map(
        ([feedDeliveries, chickDeliveries, discounts]) =>
          (this.toggleIncludeInCalculationsFeedDeliveries && feedDeliveries.length > 0) ||
          (this.toggleValueChickDeliveries && chickDeliveries.length > 0) ||
          (this.toggleValueDiscounts && discounts.length > 0),
      ),
    );
  }

  ngOnInit(): void {
    this.feedDeliveries$.pipe(takeUntil(this.destroyed$)).subscribe((feedDeliveries) => {
      this.feedDeliveriesTableConfig = {
        ...this.feedDeliveriesTableConfig,
        dataSource: new MatTableDataSource<DeliveryDataRow>(feedDeliveries),
      };
    });

    this.chickDeliveries$.pipe(takeUntil(this.destroyed$)).subscribe((chickDeliveries) => {
      // temporarily disable Edit action for Chick Deliveries table
      this.chickDeliveriesTableConfig = {
        ...this.finalSetupTableConfig,
        actions: [],
        dataSource: new MatTableDataSource<FinalSetupDataRow>(chickDeliveries),
      };
    });

    this.departures$.pipe(takeUntil(this.destroyed$)).subscribe((departures) => {
      this.departuresTableConfig = {
        ...this.finalSetupTableConfig,
        dataSource: new MatTableDataSource<FinalSetupDataRow>(departures),
      };
    });

    this.discounts$.pipe(takeUntil(this.destroyed$)).subscribe((discounts) => {
      this.discountsTableConfig = {
        ...this.discountsTableConfig,
        dataSource: new MatTableDataSource<DiscountDataRow>(discounts),
      };
    });

    this.additionalInvoices$.pipe(takeUntil(this.destroyed$)).subscribe((additionalInvoices) => {
      this.additionalInvoicesTableConfig = {
        ...this.additionalInvoicesTableConfig,
        dataSource: new MatTableDataSource<FinalSetupDataRow>(additionalInvoices),
      };
    });

    merge(this.feedDeliveryEndDateControl.valueChanges, this.fixedPriceFormGroup.valueChanges)
      .pipe(takeUntil(this.destroyed$), debounceTime(1500))
      .subscribe(() => this.updateFinalSetup());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getDeliveryDateFrom(selectedCycleNumber: number, cycles: Cycle[]): Date {
    const previousCycle = this.getPreviousCycle(selectedCycleNumber, [...cycles]);
    const previousCycleEndDate =
      previousCycle?.finalSetup?.cycleFeedDeliveryEndDate ?? previousCycle?.cycleEndDate;
    return previousCycleEndDate ? new Date(previousCycleEndDate) : DateHelper.minDate();
  }

  private getDeliveryDateTo(selectedCycle: Cycle): Date {
    const cycleEndDate =
      selectedCycle.finalSetup?.cycleFeedDeliveryEndDate ?? selectedCycle.cycleEndDate;
    return cycleEndDate ? new Date(cycleEndDate) : new Date();
  }

  private getPreviousCycle(selectedCycleNumber: number, cycles: Cycle[]): Cycle | null {
    // sort cycles descending
    cycles.sort((a, b) => b.cycleNumber - a.cycleNumber);
    if (cycles.length > 1) {
      for (const cycle of cycles) {
        if (cycle.cycleNumber < selectedCycleNumber) {
          return cycle;
        }
      }
    }

    return null;
  }

  private calculateAmountIncludingVat(amount: number, vatPercentage?: number) {
    if (!amount) {
      return null;
    }

    if (vatPercentage) {
      const amountIncludingVat = amount + (vatPercentage / 100) * amount;

      return Math.round(amountIncludingVat * 100) / 100;
    }

    return amount;
  }

  private calculateAmountChickDelivery(dayOldChicks: DayOldChick): number {
    if (dayOldChicks.amountOfChicks > 0) {
      let amountExcludingVat = dayOldChicks.chicksPrice * dayOldChicks.amountOfChicks;
      amountExcludingVat +=
        (dayOldChicks.vaccinationPricePerChick ?? 0) * dayOldChicks.amountOfChicks;
      amountExcludingVat -= (dayOldChicks.discountPricePerChick ?? 0) * dayOldChicks.amountOfChicks;
      amountExcludingVat += (dayOldChicks.diversePricePerChick ?? 0) * dayOldChicks.amountOfChicks;

      return Math.round(amountExcludingVat * 100) / 100;
    }

    return null;
  }

  private calculateAmountIncludingVatChickDelivery(dayOldChicks: DayOldChick): number {
    const amountExcludingVat = this.calculateAmountChickDelivery(dayOldChicks);
    if (amountExcludingVat) {
      return (dayOldChicks.vatPercentage / 100) * amountExcludingVat + amountExcludingVat;
    }

    return null;
  }

  updateFinalSetup() {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      this.store.dispatch(
        cycleActions.upsertCycleGeneral({
          cycle: {
            ...selectedCycle,
            finalSetup: {
              ...selectedCycle.finalSetup,
              includeFeedDeliveries: this.toggleIncludeInCalculationsFeedDeliveries,
              includeChickDeliveries: this.toggleValueChickDeliveries,
              includeDepartures: this.toggleValueDepartures,
              includeDiscounts: this.toggleValueDiscounts,
              cycleFeedDeliveryEndDate: Utils.parseDateTimeFromFormValue(
                this.feedDeliveryEndDateControl.value,
              ),
              fixedPrice: {
                ...this.fixedPriceFormGroup.value,
                invoiceDate: Utils.parseDateTimeFromFormValue(
                  this.fixedPriceFormGroup.value.invoiceDate,
                ),
              },
            },
          },
        }),
      );
    });
  }

  openImportDialog() {
    this.selectedCycle$
      .pipe(withLatestFrom(this.unlinkedSlaughterInvoices$), take(1))
      .subscribe(([selectedCycle, unlinkedSlaughterInvoices]) => {
        this.dialogService
          .fullscreenDialog(
            ImportFromSlaughterInvoiceComponent,
            {
              fullscreen: true,
              cycleId: selectedCycle.id,
              slaughterInvoices: unlinkedSlaughterInvoices,
            },
            {
              maxWidth: 'auto',
            },
          )
          .pipe(takeUntil(this.destroyed$))
          .subscribe((dialogResult) => {
            if (dialogResult.status === DIALOG_RETURN_TYPES.confirm) {
              this.fetchSlaughterInvoices(selectedCycle);
            }
          });
      });
  }

  printFinalSetup() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT ${this.locale.translate(
      'final-setup-setting.title',
    )} - ${this.datePipe.transform(String(new Date()))}`;
    if (globalThis) {
      document.title = proposedTitle;
      globalThis.print();
      document.title = existingTitle;
    }
  }

  private fetchSlaughterInvoices(selectedCycle: Cycle) {
    const fromDate = this.datePipe.transform(
      selectedCycle.cycleDate ? new Date(selectedCycle.cycleDate) : new Date(),
      'yyyy-MM-dd',
    );
    this.store.dispatch(
      cycleActions.loadSlaughterInvoicesRequest({
        fromDate,
        linkedCycleId: selectedCycle.id,
      }),
    );
    this.store.dispatch(
      cycleActions.loadSlaughterInvoicesRequest({
        fromDate,
        linkedCycleId: null,
      }),
    );
  }

  private sumAmounts(rows: FinalSetupDataRow[]): number {
    return rows.reduce((accumulator, object) => accumulator + object.amount, 0);
  }

  private sumAmountsIncludingVat(rows: FinalSetupDataRow[]): number {
    return rows.reduce((accumulator, object) => accumulator + object.amountIncludingVat, 0);
  }

  private sumVatAmounts(rows: FinalSetupDataRow[]): number {
    return rows.reduce((accumulator, object) => accumulator + object.vatAmount, 0);
  }

  private sumBaseQuantity(rows: DeliveryDataRow[]): number {
    return rows.reduce((accumulator, object) => accumulator + object.baseQuantity, 0);
  }

  private sortByDate<T extends FinalSetupDataRow>(rows: T[]): T[] {
    return rows.sort(
      (a, b) => new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime(),
    );
  }

  private groupItemsByKey<T, TKey extends PropertyKey>(
    items: T[],
    getKey: (item: T) => TKey,
  ): Record<TKey, T[]> {
    return items.reduce(
      (groups, item) => {
        const key = getKey(item);
        groups[key] = (groups[key] ?? []).concat(item);

        return groups;
      },
      {} as Record<TKey, T[]>,
    );
  }

  editItem(finalSetupRow: FinalSetupDataRow) {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      this.dialogService
        .fullscreenDialog(FinalSetupEditItemComponent, {
          confirmButton: true,
          confirmButtonText: this.locale.translate('edit'),
          pushHistoryState: false,
          finalSetupRow,
          cycleId: selectedCycle.id,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe();
    });
  }

  editAdditionalInvoice(additionalInvoiceRow: AdditionalInvoiceDataRow) {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      this.dialogService
        .fullscreenDialog(FinalSetupEditAdditionalInvoiceComponent, {
          confirmButton: true,
          confirmButtonText: this.locale.translate('edit'),
          pushHistoryState: false,
          additionalInvoiceRow,
          cycleId: selectedCycle.id,
          mode: EditAdditionalInvoiceMode.Edit,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe();
    });
  }

  addAdditionalInvoice() {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      this.dialogService
        .fullscreenDialog(FinalSetupEditAdditionalInvoiceComponent, {
          confirmButton: true,
          confirmButtonText: this.locale.translate('add'),
          pushHistoryState: false,
          cycleId: selectedCycle.id,
          mode: EditAdditionalInvoiceMode.Add,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe();
    });
  }

  deleteAdditionalInvoice(invoiceRow: AdditionalInvoiceDataRow) {
    this.dialogService
      .confirmWarning({
        title: this.locale.translate('final-setup.additional-invoices-delete-confirm-title'),
        text: this.locale.translate('final-setup.additional-invoices-delete-confirm-description'),
      })
      .pipe(
        tap((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
              this.store.dispatch(
                cycleActions.upsertCycleGeneral({
                  cycle: {
                    ...selectedCycle,
                    finalSetup: {
                      ...selectedCycle.finalSetup,
                      additionalInvoices: selectedCycle.finalSetup.additionalInvoices.filter(
                        (a) => a.id !== invoiceRow.id,
                      ),
                    },
                  },
                }),
              );
            });
          }
        }),
        take(1),
      )
      .subscribe();
  }
}
